// MA-775 (RED M2) — the age → start-module table, as a LEAF module.
//
// Moved verbatim from placementEngine.ts (which re-exports every symbol, so
// its importers are unchanged). This file imports ONLY the curriculum module
// lists and the band resolver — never placementEngine, placementValidation or
// any other placement/legacy-v2 module — so src/App/progress.ts can hand the
// mission pick the age-seed facts (ageSeedModules) without making the
// ADR-069 / MA-131 banned modules transitively reachable from the mastery
// pipelines (useSkillGraph, progression). Keep it a leaf.
import { MATH_MODULES } from "../curriculum/mathModules.js";
import { READING_MODULES } from "../curriculum/readingModules.js";
import type { ParentPriors } from "./types.ts";
import { ageToBand, ageBandLowerEdge, type AgeBandLowerEdge } from "./ageBand.ts";

export interface AgeDefaults {
  mathModuleId: string;
  readingModuleId: string;
  mathBand: string;
  readingBand: string;
  ageBand: AgeBandLowerEdge;
}

interface CurriculumModule { id: string }

/** The placement band for an age: the LOWER EDGE of the canonical band
 *  `ageToBand` resolves ("6.0-6.5" → "6.0", "7.5+" → "7.5").
 *
 *  MA-79 (ADR-106; operator ruling D-1001-6): this used to keep private
 *  thresholds at x.25 / x.75 and an untyped "7.5". It now delegates, so the
 *  starting modules match the band the parent picked. Behaviour delta, by
 *  rule: an age in [x.25, x.5) or [x.75, x+1.0) starts half a band lower
 *  than before (e.g. Settings' "4 yrs" midpoint 4.25: "4.5" → "4.0"); whole
 *  and half ages (4, 4.5, … 7.5 — onboarding's 4/5/6/7) are unchanged. Pinned
 *  by test:ma79-placement-lower-edge. A non-finite age is still treated as 6. */
export function placementBandForAge(ageValue: number | string): AgeBandLowerEdge {
  const age = Number(ageValue);
  const safeAge = Number.isFinite(age) ? age : 6;
  return ageBandLowerEdge(ageToBand(safeAge));
}

export function defaultsForAge(age: number | string): AgeDefaults {
  const band = placementBandForAge(age);
  // Pre-arithmetic defaults for age 4 — start with counting objects and recognizing numerals.
  if (band === "4.0") return { mathModuleId: "math.00a", readingModuleId: "reading.01", mathBand: "M0", readingBand: "R0", ageBand: band };
  if (band === "4.5") return { mathModuleId: "math.00b", readingModuleId: "reading.02", mathBand: "M0", readingBand: "R0", ageBand: band };
  if (band === "5.0") return { mathModuleId: "math.04", readingModuleId: "reading.02", mathBand: "M1", readingBand: "R1", ageBand: band };
  if (band === "5.5") return { mathModuleId: "math.05", readingModuleId: "reading.04", mathBand: "M1", readingBand: "R1", ageBand: band };
  if (band === "6.0") return { mathModuleId: "math.09", readingModuleId: "reading.06", mathBand: "M2", readingBand: "R2", ageBand: band };
  if (band === "6.5") return { mathModuleId: "math.10", readingModuleId: "reading.09", mathBand: "M3", readingBand: "R3", ageBand: band };
  return { mathModuleId: "math.11", readingModuleId: "reading.11", mathBand: "M3", readingBand: "R4", ageBand: band };
}

/**
 * Apply parent-prior boosts to age-default modules. Parents who tell us
 * during onboarding that their child can already add within 20 or read
 * full sentences are signaling work-ahead-of-grade-level (Kumon, gifted,
 * advanced kindergarten). Without this, a 6.5yo Kumon kid was forced to
 * start on age-default content (CVC encode, addition within 10) until
 * placement caught up — and even placement was capped at age + 1 rung.
 *
 * Conservative shifts: each "yes" answer at the ahead-of-band level
 * bumps the corresponding domain by 1 rung. Two "yes" answers across
 * two domains reinforces the signal but each domain shifts independently.
 * "not_sure" or "no" → 0 shift.
 *
 * The placement check still runs after, so an over-optimistic parent
 * answer is corrected within minutes.
 */
export function applyPriorBoost(defaults: AgeDefaults, priors: ParentPriors | null | undefined): AgeDefaults {
  if (!priors || typeof priors !== "object") return defaults;
  const mathShift = priors.canAddWithin20 === "yes" ? 1 : 0;
  const readingShift = priors.canReadSentences === "yes" ? 1 : 0;
  if (mathShift === 0 && readingShift === 0) return defaults;
  return {
    ...defaults,
    mathModuleId: shiftModule(MATH_MODULES, defaults.mathModuleId, mathShift),
    readingModuleId: shiftModule(READING_MODULES, defaults.readingModuleId, readingShift),
  };
}

/** Walk `delta` rungs along a module ladder, clamped at both ends; an
 *  unknown id is returned unchanged. */
export function shiftModule(modules: CurriculumModule[], moduleId: string, delta: number): string {
  const idx = modules.findIndex((m) => m.id === moduleId);
  if (idx < 0) return moduleId;
  const nextIdx = Math.max(0, Math.min(modules.length - 1, idx + delta));
  return modules[nextIdx].id;
}
